import { ContentRequirement } from '@foodfluence/shared';

/**
 * Gets accepted file types based on content requirement
 */
export const getAcceptedFileTypes = (contentRequirement?: ContentRequirement) => {
  if (contentRequirement?.type === 'video') {
    return {
      'video/*': ['.mp4', '.mov', '.avi', '.wmv']
    };
  } else if (contentRequirement?.type === 'photo') {
    return {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    };
  } else {
    return {
      'video/*': ['.mp4', '.mov', '.avi', '.wmv'],
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'application/pdf': ['.pdf']
    };
  }
};

/**
 * Gets file input accept string based on content requirement
 */
export const getFileInputAccept = (contentRequirement?: ContentRequirement): string => {
  if (contentRequirement?.type === 'video') {
    return 'video/*';
  } else if (contentRequirement?.type === 'photo') {
    return 'image/*';
  }
  return 'image/*,video/*,.pdf';
};

/**
 * Creates a file input element and triggers file selection
 */
export const triggerFileSelection = (
  contentRequirement: ContentRequirement,
  onFileSelect: (file: File) => void
) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = getFileInputAccept(contentRequirement);
  input.onchange = (e) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files[0]) {
      onFileSelect(target.files[0]);
    }
  };
  input.click();
};

/**
 * Formats file size in MB
 */
export const formatFileSize = (bytes: number): string => {
  return (bytes / 1024 / 1024).toFixed(2);
};

/**
 * Checks if a file is an image
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * Checks if a file is a video
 */
export const isVideoFile = (file: File): boolean => {
  return file.type.startsWith('video/');
};

/**
 * Gets file type display text for upload areas
 */
export const getFileTypeDisplayText = (contentRequirement?: ContentRequirement): string => {
  if (contentRequirement?.type === 'video') {
    return 'Videos (MP4, MOV, AVI, WMV) only';
  } else if (contentRequirement?.type === 'photo') {
    return 'Images (JPG, PNG, GIF) only';
  }
  return 'Images (JPG, PNG, GIF), Videos (MP4, MOV, AVI), PDFs';
};

/**
 * Uploads a file with progress tracking
 */
export const uploadWithProgress = async (
  url: string, 
  file: File, 
  onProgress: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Checks if content container has overflow and needs scroll buttons
 */
export const checkContentOverflow = (containerId: string): boolean => {
  const container = document.getElementById(containerId);
  if (container) {
    return container.scrollWidth > container.clientWidth;
  }
  return false;
};

/**
 * Scrolls content container horizontally
 */
export const scrollContentContainer = (containerId: string, direction: 'left' | 'right', amount: number = 300) => {
  const container = document.getElementById(containerId);
  if (container) {
    const scrollAmount = direction === 'left' ? -amount : amount;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }
};
